/**
 * Browser Bugsnag notifier — ATLAS reporting its own client-side crashes.
 *
 * The key is a *separate* project key from the server's `BUGSNAG_API_KEY`,
 * because Bugsnag scopes a notifier key to one project and one platform. It is
 * baked into the bundle at build time, which is fine and by design: a notifier
 * key only grants the ability to *send* events to one project. It cannot read
 * anything. Do not reach for the Data Access token here — that one is a real
 * secret and must never leave the server.
 *
 * Dormant unless `VITE_BUGSNAG_API_KEY` was set at build time, so a local or
 * unconfigured build behaves exactly as it did before this module existed.
 */
import Bugsnag, { type Plugin } from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';
import React from 'react';

const apiKey = import.meta.env.VITE_BUGSNAG_API_KEY as string | undefined;

let started = false;

/**
 * A build-identity `<meta>` from index.html, or undefined when it was never
 * stamped.
 *
 * The identity lives in index.html, not in `import.meta.env`, so that
 * `vite build` sees the same inputs on every deploy and its layer comes
 * straight from the PR's image cache (see docker/Dockerfile.frontend). A local
 * `npm run dev` serves the unstamped placeholder, which reads as absent.
 */
export function readBuildMeta(name: 'atlas-app-version' | 'atlas-app-git-hash'): string | undefined {
  const value = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`)?.content;
  return value && !value.startsWith('__') ? value : undefined;
}

/**
 * Start the notifier. Call once, as early in `main.tsx` as possible — anything
 * that throws before this runs is unreported.
 */
export function initBugsnag(): void {
  if (started || !apiKey) return;

  const appVersion = readBuildMeta('atlas-app-version');
  const gitHash = readBuildMeta('atlas-app-git-hash');

  try {
    Bugsnag.start({
      apiKey,
      // ponytail: plugin-react 9.0.0 still peers @bugsnag/core ^8, so npm never
      // hoists core 9 where the plugin's types can see it and its `Plugin`
      // base resolves to nothing. Same release as @bugsnag/js, so runtime is
      // fine. Drop the cast once upstream widens the peer range.
      plugins: [new BugsnagPluginReact(React) as unknown as Plugin],
      // The deploy's build stamp — the same value the server reports, so one
      // release reads the same on both halves. Without it Bugsnag files every
      // session under no release and the stability view stays empty.
      ...(appVersion ? { appVersion } : {}),
      ...(gitHash ? { metadata: { build: { gitHash } } } : {}),
      // Mirrors the server's stages so one release reads the same on both
      // halves. VITE_ENV_LABEL is the existing per-host banner var: set to
      // "DEV" on the dev droplet, unset on prod.
      releaseStage: import.meta.env.DEV
        ? 'development'
        : (import.meta.env.VITE_ENV_LABEL as string | undefined)?.toLowerCase() || 'production',
      enabledReleaseStages: ['production', 'development'],
      redactedKeys: [/authorization/i, /cookie/i, /password/i, /secret/i, /token/i],
      /**
       * Drop events the app cannot act on. A failed `fetch` from a laptop that
       * slept, and extension-injected script errors, are the two highest-volume
       * classes of browser noise and neither is an ATLAS defect.
       */
      onError(event) {
        const message = event.errors[0]?.errorMessage ?? '';
        if (/Failed to fetch|NetworkError|Load failed/i.test(message)) return false;
        if (/chrome-extension:|moz-extension:/i.test(message)) return false;
        return true;
      },
    });
    started = true;
  } catch {
    // An observability tool must never be the reason the app fails to boot.
  }
}

/** Whether events are actually being sent. */
export const isBugsnagEnabled = (): boolean => started;

/**
 * Attach the signed-in user to subsequent events, so an error report says who
 * hit it. ATLAS has ~30 named users; "one user is affected" is only useful when
 * you know which one.
 *
 * Called from AuthContext on login and on logout (with no argument, which
 * clears the attribution rather than leaving the previous user attached).
 */
export function setBugsnagUser(user?: { userId: string; email?: string; name?: string }): void {
  if (!started) return;
  try {
    Bugsnag.setUser(user?.userId, user?.email, user?.name);
  } catch {
    // Non-fatal: attribution is a nicety, not a reason to break the app.
  }
}

/** Report a caught error that the app handled but a human should still see. */
export function notifyBugsnag(err: unknown, metadata?: Record<string, unknown>): void {
  if (!started) return;
  try {
    const error = err instanceof Error ? err : new Error(String(err));
    Bugsnag.notify(error, (event) => {
      if (metadata) event.addMetadata('context', metadata);
    });
  } catch {
    // See above.
  }
}
